import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router';

/**
 * Resets scroll to the top of the page on route changes.
 *
 * Without this, navigating between routes in the SPA keeps the previous
 * page's scroll position — so clicking a blog card lower down a list lands
 * you partway down the article.
 *
 * Back/forward navigation (POP) is left alone so the browser's own scroll
 * restoration still works as users expect. Renders nothing.
 */
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === 'POP') return;

    // The scrolling element is #root, NOT the window. index.css sets
    // `body { height: 100vh; overflow: hidden }` and
    // `#root { height: 100vh; overflow-y: auto }`, so the document never
    // scrolls and window.scrollY is permanently 0. This effect was firing on
    // every navigation but window.scrollTo(0, 0) was a no-op against a
    // container that was already at 0 — which is why landing mid-page felt
    // like scroll restoration was simply missing.
    document.getElementById('root')?.scrollTo(0, 0);

    // Kept as a fallback for any context where the document IS the scroller —
    // e.g. if the #root overflow rule is ever removed, or a print/embed view.
    window.scrollTo(0, 0);
  }, [pathname, navigationType]);

  return null;
};

export default ScrollToTop;
